/* eslint-disable react/prop-types, new-cap */
import { LicenseReport } from '@augmentt-dev/ui-components'
import { Amplify } from 'aws-amplify'
import { signOut as amplifySignOut, fetchAuthSession } from 'aws-amplify/auth'
import passwordGenerator from 'generate-password-browser'
import React from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import * as Yup from 'yup'
import awsConfig from '../pages/auth/aws-exports'

import * as builder from '../../_augmentt/ducks/builder'
import * as customers from '../../_augmentt/ducks/customers'
import * as licenses from '../../_augmentt/ducks/licenses'
import * as meta from '../../_augmentt/ducks/meta'
import * as sessionModal from '../../_augmentt/ducks/sessionModal'
import * as uploads from '../../_augmentt/ducks/uploads'
import * as user from '../../_augmentt/ducks/user'

import { getMenuConfig } from '../../_augmentt/layout/MenuConfig'
import { customerCollectionsApi } from '../../_augmentt/services/customerCollections'
import { featureFlagsApi } from '../../_augmentt/services/featureFlags'
import { hubApi } from '../../_augmentt/services/hub'
import { intuneAppPoliciesApi } from '../../_augmentt/services/intuneAppPolicies'
import { intuneDeviceManagementScriptsApi } from '../../_augmentt/services/intuneDeviceManagementScripts'
import { intuneMobileAppConfigurationsApi } from '../../_augmentt/services/intuneMobileAppConfigurations'
import { intuneAndroidAppConfigurationSchemasApi } from '../../_augmentt/services/intuneAndroidAppConfigurationSchemas'
import { intuneMobileAppsApi } from '../../_augmentt/services/intuneMobileApps'
import { secureApi } from '../../_augmentt/services/secure'

export const NO_SPACE_REGEX_EXP = /^\S+$/

export const PASSWORD_REGEX_EXP =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^&*()+_\-=}{[\]|:;"/?.><,`~ ]).{8,}$/

export const PHONE_REGEX_EXP =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/

export const URL_REGEX_EXP = /^(http(s):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/ // eslint-disable-line no-useless-escape

// @TODO: why we have a second check here for the menu visibility?
export const allowPage = (path, allowPermissions) => {
  // this is being controlled LayoutInitializer->displayPage
  if (path.includes('secure')) {
    return true
  }

  if (path === 'users') {
    // eslint-disable-next-line no-param-reassign
    path = 'systemUsers'
  } else if (path.includes('licenses')) {
    // eslint-disable-next-line no-param-reassign
    path = 'licenseCenter'
  } else if (path.includes('look-and-feel')) {
    // eslint-disable-next-line no-param-reassign
    path = 'lookAndFeel'
  } else if (path.includes('discovery') || path.includes('discover')) {
    // eslint-disable-next-line no-param-reassign
    path = 'discover'
  }

  return !(allowPermissions[path] && allowPermissions[path] === 'disabled')
}

export const getConfirmPasswordSchema = (intl, oneOfRef) =>
  new Yup.string()
    .min(
      8,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.TOO_SHORT',
      }),
    )
    .oneOf(
      [Yup.ref(oneOfRef), null],
      intl.formatMessage({
        id: 'AUTH.VALIDATION.PASSWORD_MISMATCH',
      }),
    )
    .required(
      intl.formatMessage({
        id: 'AUTH.VALIDATION.REQUIRED_FIELD',
      }),
    )

export const getPasswordSchema = (intl) =>
  new Yup.string()
    .min(
      8,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.TOO_SHORT',
      }),
    )
    .required(
      intl.formatMessage({
        id: 'AUTH.VALIDATION.REQUIRED_FIELD',
      }),
    )

export const getNewPasswordSchema = (intl) =>
  // START_WITH_SPACE
  new Yup.string()
    .matches(
      NO_SPACE_REGEX_EXP,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.NO_SPACE',
      }),
    )
    .min(
      8,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.TOO_SHORT',
      }),
    )
    .required(
      intl.formatMessage({
        id: 'AUTH.VALIDATION.REQUIRED_FIELD',
      }),
    )
    .matches(
      PASSWORD_REGEX_EXP,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.INVALID_PASSWORD',
      }),
    )

export const getEmailSchema = (intl) =>
  new Yup.string()
    .email(
      intl.formatMessage({
        id: 'AUTH.VALIDATION.INVALID_EMAIL',
      }),
    )
    .required(
      intl.formatMessage({
        id: 'AUTH.VALIDATION.REQUIRED_FIELD',
      }),
    )

export const getNonRequiredEmailSchema = (intl) =>
  new Yup.string().email(
    intl.formatMessage({
      id: 'AUTH.VALIDATION.INVALID_EMAIL',
    }),
  )

export const getFirstNameSchema = (intl) =>
  new Yup.string()
    .min(
      2,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.TOO_SHORT',
      }),
    )
    .max(
      50,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.TOO_LONG',
      }),
    )
    .matches(
      /^[a-zA-Z -]*$/,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.NO_SPECIAL_CHARACTERS',
      }),
    )
    .required(
      intl.formatMessage({
        id: 'AUTH.VALIDATION.REQUIRED_FIELD',
      }),
    )

export const getLastNameSchema = (intl) =>
  new Yup.string()
    .min(
      2,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.TOO_SHORT',
      }),
    )
    .max(
      50,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.TOO_LONG',
      }),
    )
    .matches(
      /^[a-zA-Z -]*$/,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.NO_SPECIAL_CHARACTERS',
      }),
    )
    .required(
      intl.formatMessage({
        id: 'AUTH.VALIDATION.REQUIRED_FIELD',
      }),
    )

export const getCompanyNameSchema = (intl) =>
  new Yup.string()
    .min(
      2,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.TOO_SHORT',
      }),
    )
    .max(
      50,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.TOO_LONG',
      }),
    )
    .required(
      intl.formatMessage({
        id: 'AUTH.VALIDATION.REQUIRED_FIELD',
      }),
    )

export const getPhoneSchema = (intl) =>
  new Yup.string()
    .matches(
      PHONE_REGEX_EXP,
      intl.formatMessage({
        id: 'AUTH.VALIDATION.INVALID_PHONE_NUMBER',
      }),
    )
    .required(
      intl.formatMessage({
        id: 'AUTH.VALIDATION.REQUIRED_FIELD',
      }),
    )

export async function signOut(dispatch) {
  Amplify.configure(awsConfig)
  try {
    // Clear RTK Query cache on sign-out so the next user gets fresh feature flags.
    dispatch(featureFlagsApi.util.resetApiState())
    dispatch(secureApi.util.resetApiState())
    dispatch(customerCollectionsApi.util.resetApiState())
    dispatch(intuneAppPoliciesApi.util.resetApiState())
    dispatch(intuneDeviceManagementScriptsApi.util.resetApiState())
    dispatch(intuneMobileAppConfigurationsApi.util.resetApiState())
    dispatch(intuneMobileAppsApi.util.resetApiState())
    dispatch(intuneAndroidAppConfigurationSchemasApi.util.resetApiState())
    dispatch(hubApi.util.resetApiState())

    // Reset info in redux store
    dispatch(user.actions.setUser())
    dispatch(customers.actions.setCustomers([]))
    dispatch(uploads.actions.setFiles([]))
    dispatch(licenses.actions.setLicenses())
    dispatch(meta.actions.setHasValidSubscription(null))
    dispatch(meta.actions.setChargebeeId(null))
    dispatch(meta.actions.setHasValidM365LicensingReportSubscription(null))
    dispatch(meta.actions.setHasValidPostureBaselineSubscription(null))
    dispatch(meta.actions.setCloudallyUrl(null))
    dispatch(meta.actions.setSecurityTrainingUrl(null))
    dispatch(customers.actions.setSelectedCustomer(null))
    dispatch(sessionModal.actions.closeSessionModal())
    dispatch(builder.actions.setMenuConfig(getMenuConfig()))
    localStorage.removeItem(LicenseReport.localStorageKey)
    if (window.Hixel) window.Hixel.clearIdentity()

    await amplifySignOut()

    return true
  } catch (e) {
    console.error(e)
    return false
  }
}

const openSessionModal = (dispatch, intl) => {
  dispatch(
    sessionModal.actions.openSessionModal({
      description: intl.formatMessage({
        id: 'AUTH.SESSIONTIMEOUT.SIGN_OUT_MESSAGE',
      }),
    }),
  )
}

export const verifySession = async ({ dispatch, intl, awsRum }) => {
  Amplify.configure(awsConfig)

  try {
    await fetchAuthSession({ forceRefresh: true })
  } catch (e) {
    console.error(`Error verifying session, ${e}`)
    awsRum?.recordError(e)
    openSessionModal(dispatch, intl)
  }
}

// Display a lock or not
export function Lock(props) {
  const { intl, display, messageId } = props

  let message = 'Locked'
  if (messageId)
    message = intl.formatMessage({ id: 'UPGRADE.TO' }).replace('{plan}', intl.formatMessage({ id: messageId }))

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {display === true && (
        <span className="menu__link-badge" style={{ paddingLeft: '1rem' }}>
          <OverlayTrigger placement="right" overlay={<Tooltip id="lock-tooltip">{message}</Tooltip>}>
            <span className="badge-lg fa fa-lock" />
          </OverlayTrigger>
        </span>
      )}
    </>
  )
}

export const generatePassword = () =>
  passwordGenerator.generate({
    length: 16,
    numbers: true,
    symbols: true,
    lowercase: true,
    uppercase: true,
    strict: true,
  })
